import React from 'react';
import styles from './CftcDashboardLayout.module.css';

type CftcDashboardLayoutProps = {
  summaryPills?: React.ReactNode;
  summaryCard: React.ReactNode;
  netPositioningGuide?: React.ReactNode;
  positioningVsPriceGuide?: React.ReactNode;
  netPositioningHeaderRight?: React.ReactNode;
  netPositioningCardTitle?: React.ReactNode;
  netPositioningSectionTitle?: React.ReactNode;
  netPositioningMetaLine?: React.ReactNode;
  deskReadCard: React.ReactNode;
  positioningChart: React.ReactNode;
  positioningChartSummary?: React.ReactNode;
  positioningChartBadge?: React.ReactNode;
  correlationSummary: React.ReactNode;
  correlationChart: React.ReactNode;
  priceChart?: React.ReactNode;
  positioningTitleRef?: React.Ref<HTMLHeadingElement>;
};

const CftcDashboardLayout: React.FC<CftcDashboardLayoutProps> = ({
  summaryPills,
  summaryCard,
  netPositioningGuide,
  positioningVsPriceGuide,
  netPositioningHeaderRight,
  netPositioningCardTitle,
  netPositioningSectionTitle,
  netPositioningMetaLine,
  deskReadCard,
  positioningChart,
  positioningChartSummary,
  positioningChartBadge,
  correlationSummary,
  correlationChart,
  priceChart,
  positioningTitleRef,
}) => {
  const showSummary = summaryPills || summaryCard;

  return (
    <div className={styles.container}>
      <div className={styles.stack}>
        {showSummary && (
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Summary</h2>
              <div className={styles.pillsRow}>{summaryPills}</div>
            </div>
            <div className={styles.sectionBody}>{summaryCard}</div>
          </section>
        )}

        <section className={styles.section}>
          {(netPositioningSectionTitle || netPositioningMetaLine) && (
            <div className={styles.sectionHeader}>
              <div className={styles.sectionHeaderLeft}>
                <h2 className={styles.sectionTitle} data-onboard="position-trends-title">
                  {netPositioningSectionTitle || 'Net Positioning Trends'}
                </h2>
                {netPositioningMetaLine && (
                  <div className={styles.metaText}>{netPositioningMetaLine}</div>
                )}
              </div>
              <div className={styles.metaText}>{netPositioningHeaderRight}</div>
            </div>
          )}
          {deskReadCard ? (
            <div className={`${styles.sectionBody} ${styles.grid}`}>
              <div className={styles.gridCol4}>{deskReadCard}</div>
              <div className={styles.gridCol8}>
                {netPositioningGuide && (
                  <div className={styles.sectionGuideRow}>{netPositioningGuide}</div>
                )}
                <div className={styles.chartCard}>
                  <div className={styles.chartHeader}>
                    <div className={styles.chartTitle} data-onboard="position-trends-title">
                      {netPositioningCardTitle || 'Positioning trend'}
                    </div>
                    <div className={styles.chartHeaderMeta}>
                      {netPositioningHeaderRight}
                      {positioningChartBadge}
                    </div>
                  </div>
                  {positioningChartSummary && (
                    <div className={styles.chartSummary} data-onboard="position-trends-summary">
                      {positioningChartSummary}
                    </div>
                  )}
                  <div className={styles.chartBody} data-onboard="position-trends-chart">
                    {positioningChart}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.sectionBody}>
              {netPositioningGuide && (
                <div className={styles.sectionGuideRow}>{netPositioningGuide}</div>
              )}
              <div className={styles.chartCard}>
                <div className={styles.chartHeader}>
                  <div className={styles.chartTitle} data-onboard="position-trends-title">
                    {netPositioningCardTitle || 'Positioning trend'}
                  </div>
                  <div className={styles.chartHeaderMeta}>
                    {netPositioningHeaderRight}
                    {positioningChartBadge}
                  </div>
                </div>
                {positioningChartSummary && (
                  <div className={styles.chartSummary} data-onboard="position-trends-summary">
                    {positioningChartSummary}
                  </div>
                )}
                <div className={styles.chartBody} data-onboard="position-trends-chart">
                  {positioningChart}
                </div>
              </div>
            </div>
          )}
        </section>

        <section className={styles.section}>
          {positioningVsPriceGuide && (
            <div className={styles.sectionGuideRow}>{positioningVsPriceGuide}</div>
          )}
          <div className={styles.chartCard}>
            <div className={styles.chartHeader}>
              <div className={styles.chartTitleRow} ref={positioningTitleRef}>
                <span className={styles.sectionTitle}>Positioning vs. Price</span>
                <span className={styles.infoIcon} tabIndex={0} aria-label="Positioning vs. Price info">
                  ?
                  <span className={styles.infoTooltip}>
                    <span className={styles.infoText}>
                      shows how weekly positioning changes relate to future price returns (not
                      same-day price moves). It helps answer: “Does positioning tend to lead price,
                      or does price lead positioning?”
                    </span>
                    <span className={styles.infoTitle}>What’s inside:</span>
                    <ul className={styles.infoList}>
                      <li>
                        Metric
                        <ul>
                          <li>ΔNet: weekly change in net positioning</li>
                          <li>ΔNet % OI: change normalized by open interest</li>
                        </ul>
                      </li>
                      <li>
                        Horizon (1–4w)
                        <div className={styles.infoNote}>
                          How far forward returns are measured from each CFTC week.
                        </div>
                      </li>
                      <li>
                        Lead/Lag slider (−8…+8w)
                        <ul>
                          <li>Positive = positioning leads price</li>
                          <li>Negative = price leads positioning</li>
                          <li>0 = same week (shown with a caution)</li>
                        </ul>
                      </li>
                      <li>
                        Method (Pearson/Spearman)
                        <div className={styles.infoNote}>
                          Pearson = linear; Spearman = rank/robust to outliers.
                        </div>
                      </li>
                    </ul>
                  </span>
                </span>
              </div>
            </div>
            <div className={`${styles.sectionBody} ${styles.grid}`}>
              <div className={styles.gridCol4}>{correlationSummary}</div>
              <div className={styles.gridCol8}>{correlationChart}</div>
            </div>
          </div>
        </section>

        {priceChart && <section className={styles.section}>{priceChart}</section>}
      </div>
    </div>
  );
};

export default CftcDashboardLayout;
